import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2 } from "lucide-react";
import "../shared/vrmDataTable.css";
import "./VrmClientDashboard.css";

const API_URL = process.env.REACT_APP_API_URL;

const STATIC_CARDS = [
  {
    id: "audit_checklist",
    title: "Audit Checklist",
    subtitle: "VRM-approved checklists",
    description: "Review, approve, and manage audit checklists.",
    logo: "/img/checklist-icon.svg",
    actionPath: "/audit_checklist",
  },
  {
    id: "frameworks_standards",
    title: "Frameworks & Standards",
    subtitle: "Uploaded frameworks",
    description: "View frameworks and standards for the selected client.",
    logo: "/img/documentation-icon.svg",
    actionPath: "/vrm_framework_requests",
    // Opens pre-filtered to the client picked above.
    passClient: true,
  },
  {
    id: "risk_register_requests",
    title: "Risk Register Requests",
    subtitle: "Client upload approvals",
    description: "Review uploaded risk register workbooks and approve them into CARA.",
    logo: "/img/risk-management.png",
    actionPath: "/vrm_risk_register_requests",
  },
  {
    id: "questionnaire_requests",
    title: "TPRM Questionnaires",
    subtitle: "Client questionnaire approvals",
    description: "Review uploaded questionnaire workbooks and approve them for CARA and TPRM use.",
    logo: "/img/documentation-icon.svg",
    actionPath: "/vrm_questionnaire_requests",
  },
];

const VrmClientDashboard = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [loadingClients, setLoadingClients] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeCompanyName, setActiveCompanyName] = useState("");

  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoadingClients(true);
        const response = await axios.get(`${API_URL}api/vrm/clients/`, {
          withCredentials: true,
        });
        const clientList = response.data?.clients || [];
        setClients(clientList);
      } catch (error) {
        setErrorMessage("Unable to load assigned clients.");
      } finally {
        setLoadingClients(false);
      }
    };

    fetchClients();
  }, []);

  const handleClientChange = async (event) => {
    const clientId = event.target.value;
    setSelectedClientId(clientId);
    setErrorMessage("");

    const selectedClient = clients.find((client) => client.admin_id === clientId);
    if (!selectedClient) {
      setActiveCompanyName("");
      return;
    }

    try {
      await axios.post(
        `${API_URL}api/vrm/select-client/`,
        {
          admin_id: selectedClient.admin_id,
          tenant_db_name: selectedClient.tenant_db_name,
        },
        { withCredentials: true }
      );
      setActiveCompanyName(selectedClient.company_name || "");
    } catch (error) {
      setErrorMessage("Unable to switch client context.");
      setActiveCompanyName("");
    }
  };

  const handleCardClick = (card) => {
    if (!selectedClientId) {
      setErrorMessage("Please select a company to continue.");
      return;
    }
    if (card.actionPath) {
      navigate(
        card.passClient
          ? `${card.actionPath}?admin_id=${encodeURIComponent(selectedClientId)}`
          : card.actionPath
      );
    }
  };

  return (
    <div className="content-wrapper"
      style={{ padding: '6rem 1rem 2rem', minHeight: '100vh', backgroundColor: '#F0F2FF', borderRadius: '10px' }}>
      <main id="vrm_client_dashboard">
        <div className="vdt-card">
          <header className="vdt-header">
            <div>
              <div className="vdt-header-title">
                <LayoutDashboard size={30} color="#3a80af" />
                <h1>Client Dashboard</h1>
              </div>
              <p>View client data, uploaded questionnaires, and dashboards.</p>
            </div>
          </header>

          <div className="vdt-filter-bar vdt-single-filter">
            <div className="vdt-filter-input-group">
              <label className="vdt-filter-label" htmlFor="companySelect">Client</label>
              <div className="vdt-input-icon-wrapper">
                <Building2 size={16} className="vdt-filter-icon" />
                <select
                  id="companySelect"
                  className="vdt-filter-select"
                  value={selectedClientId}
                  onChange={handleClientChange}
                  disabled={loadingClients}
                >
                  <option value="">Select company</option>
                  {clients.map((client) => (
                    <option key={client.admin_id} value={client.admin_id}>
                      {client.company_name || client.admin_id}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {loadingClients && (
            <div className="vrm-client-state">Loading client list...</div>
          )}

          {!loadingClients && clients.length === 0 && (
            <div className="vrm-client-state">No clients assigned yet.</div>
          )}

          {errorMessage && <div className="vrm-client-error">{errorMessage}</div>}

          {selectedClientId && (
            <div className="vrm-client-summary">
              <span>Active client:</span>
              <strong>{activeCompanyName || "Selected Client"}</strong>
            </div>
          )}

          <div className="vrm-client-card-grid">
            {STATIC_CARDS.map((card) => (
              <button
                key={card.id}
                type="button"
                className="vrm-client-card"
                onClick={() => handleCardClick(card)}
              >
                <div className="vrm-client-card-logo">
                  <img src={card.logo} alt={`${card.title} logo`} />
                </div>
                <div className="vrm-client-card-body">
                  <h5>{card.title}</h5>
                  <p className="vrm-client-card-subtitle">{card.subtitle}</p>
                  <p className="vrm-client-card-description">{card.description}</p>
                </div>
                <div className="vrm-client-card-footer">View More</div>
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default VrmClientDashboard;
